import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { setPageMeta } from '@/lib/seo';
import { useT, useLang } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { getCheckoutStatus } from '@/lib/api';
import { Check, Loader2, ArrowRight, Sparkles } from 'lucide-react';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ~60s

function centsToPrice(amount, currency = 'eur', lang = 'fr') {
  const value = (amount || 0) / 100;
  try {
    return new Intl.NumberFormat(lang === 'en' ? 'en-GB' : 'fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

export default function CheckoutSuccess() {
  const t = useT();
  const { lang } = useLang();
  const { refresh: refreshUser } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [status, setStatus] = useState('polling'); // polling | complete | expired | not_found | error
  const [record, setRecord] = useState(null);
  const pollsRef = useRef(0);

  useEffect(() => {
    setPageMeta({
      title: t('checkout_success.seo_title'),
      description: t('checkout_success.seo_description'),
      path: '/paiement/succes',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('not_found');
      return;
    }
    let stopped = false;
    let timer = null;

    const poll = async () => {
      if (stopped) return;
      const res = await getCheckoutStatus(sessionId);
      if (stopped) return;
      pollsRef.current += 1;
      if (!res.ok) {
        setStatus(res.code === 'not_found' ? 'not_found' : 'error');
        return;
      }
      setRecord(res);
      if (res.status === 'complete') {
        setStatus('complete');
        return;
      }
      if (res.status === 'expired' || res.status === 'failed') {
        setStatus(res.status);
        return;
      }
      if (pollsRef.current >= MAX_POLLS) {
        setStatus('timeout');
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const planLabel = record?.plan
    ? (record.plan.charAt(0).toUpperCase() + record.plan.slice(1))
    : '';
  const price = record ? centsToPrice(record.amount, record.currency, lang) : '';
  const intervalLabel =
    record?.interval === 'year'
      ? t('checkout_success.interval_year')
      : t('checkout_success.interval_month');

  return (
    <div data-testid="page-checkout-success">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-24">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute left-1/2 -top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(16,185,129,0.28), transparent 70%)' }}
            />
          </div>

          <div className="om-container max-w-3xl">
            <div
              className="rounded-[32px] border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-10 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-14"
              data-testid="checkout-success-card"
            >
              {status === 'polling' && (
                <div className="text-center" data-testid="checkout-success-polling">
                  <div className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full bg-[var(--om-ink)]/5">
                    <Loader2 size={28} strokeWidth={2.4} className="animate-spin text-[var(--om-ink)]" />
                  </div>
                  <span className="om-eyebrow">{t('checkout_success.polling_eyebrow')}</span>
                  <h1 className="om-h1 mt-4 text-[32px] sm:text-[42px] text-[var(--om-ink)]">
                    {t('checkout_success.polling_title')}
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('checkout_success.polling_subtitle')}
                  </p>
                </div>
              )}

              {status === 'complete' && (
                <div className="text-center" data-testid="checkout-success-complete">
                  <div className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
                    <Check size={30} strokeWidth={3} />
                  </div>
                  <span className="om-eyebrow" style={{ color: '#10B981' }}>
                    <Sparkles size={12} strokeWidth={2.6} className="inline mr-1 -mt-0.5" />
                    {t('checkout_success.success_eyebrow')}
                  </span>
                  <h1 className="om-h1 mt-4 text-[32px] sm:text-[44px] text-[var(--om-ink)]">
                    {t('checkout_success.success_title')}
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('checkout_success.success_subtitle')}
                  </p>
                  {record && (
                    <div className="mt-8 inline-flex flex-col items-center gap-2 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg)] px-6 py-5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('checkout_success.plan_label')}
                      </div>
                      <div className="text-2xl font-extrabold text-[var(--om-ink)]" data-testid="checkout-success-plan">
                        OpenMeta {planLabel}
                      </div>
                      <div className="text-sm text-[var(--om-muted)]" data-testid="checkout-success-price">
                        {price} · {intervalLabel}
                      </div>
                      {record.email && (
                        <div className="mt-1 text-xs text-[var(--om-muted-2)]">
                          {t('checkout_success.confirmation_sent_to')} <strong className="text-[var(--om-ink)]">{record.email}</strong>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <Link to="/" className="om-btn-primary" data-testid="checkout-success-home">
                      {t('checkout_success.cta_home')} <ArrowRight size={16} strokeWidth={2.4} />
                    </Link>
                    <Link to="/generateur" className="om-btn-ghost" data-testid="checkout-success-generator">
                      {t('checkout_success.cta_generator')}
                    </Link>
                  </div>
                </div>
              )}

              {(status === 'expired' || status === 'failed' || status === 'timeout') && (
                <div className="text-center" data-testid="checkout-success-issue">
                  <span className="om-eyebrow" style={{ color: '#F97316' }}>
                    {t('checkout_success.pending_eyebrow')}
                  </span>
                  <h1 className="om-h1 mt-4 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('checkout_success.pending_title')}
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('checkout_success.pending_subtitle')}
                  </p>
                  <div className="mt-8 flex justify-center">
                    <Link to="/tarifs" className="om-btn-ghost" data-testid="checkout-success-back-pricing">
                      {t('checkout_success.cta_back_pricing')}
                    </Link>
                  </div>
                </div>
              )}

              {status === 'not_found' && (
                <div className="text-center" data-testid="checkout-success-notfound">
                  <h1 className="om-h1 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('checkout_success.notfound_title')}
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('checkout_success.notfound_subtitle')}
                  </p>
                  <div className="mt-8 flex justify-center">
                    <Link to="/tarifs" className="om-btn-primary" data-testid="checkout-success-back-pricing">
                      {t('checkout_success.cta_back_pricing')}
                    </Link>
                  </div>
                </div>
              )}

              {status === 'error' && (
                <div className="text-center" data-testid="checkout-success-error">
                  <h1 className="om-h1 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                    {t('checkout_success.error_title')}
                  </h1>
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[var(--om-muted)]">
                    {t('checkout_success.error_subtitle')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
